import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { roundupsService } from "@/services/roundups.service";
import type { RoundupActivity, RoundupListResponse } from "@/services/roundups.service";

const ROUNDUPS_PAGE_SIZE = 20;

export const useRoundupsList = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { isAuthenticated } = useAuth();

  return useInfiniteQuery<RoundupListResponse>({
    queryKey: ["roundups", "list"],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      roundupsService.listRoundups(ROUNDUPS_PAGE_SIZE, pageParam as string | null),
    getNextPageParam: lastPage => lastPage.next_cursor ?? undefined,
    enabled: enabled && isAuthenticated,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useRoundupActivity = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { isAuthenticated } = useAuth();

  return useQuery<RoundupActivity>({
    queryKey: ["roundups", "activity"],
    queryFn: () => roundupsService.getActivity(),
    enabled: enabled && isAuthenticated,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
};

export const useRoundupDetail = (roundupId?: string) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ["roundups", "detail", roundupId],
    queryFn: () => roundupsService.getRoundup(roundupId!),
    enabled: isAuthenticated && Boolean(roundupId),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
};
